import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
  Alert,
  Divider,
  Snackbar,
} from '@mui/material'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { TextField } from 'components/@extended/TextField'
import SingleOrganizationSelector from 'components/SingleOrganizationSelector'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { DATE_FORMAT } from 'types/periods'
import useConfig from 'hooks/useConfig'
import { useFetchLedgerAccounts } from 'hooks/useLedgerAccounts'
import { useEffect, useState } from 'react'
import { lookupGuestRole, lookupMemberRole } from 'types/role'
import { AssignedRole } from '../types/types'
import { useEditUserDialog } from '../context/EditUserDialogContext'
import { useFetchOrganizationCoreSettings } from 'hooks/useOrganizations'
import useAuth from 'hooks/useAuth'
import { postWithToken } from 'hooks/http'
import { useGlobalState } from 'contexts/GlobalStateContext'

dayjs.extend(utc)

interface UserInformationProps {
  defaultOrganizationId?: string | null
  createMode: boolean
}

const UserInformationForm: React.FC<UserInformationProps> = ({ defaultOrganizationId = null, createMode = false }) => {
  const { mode } = useConfig()
  const { token: authToken } = useAuth()
  const { checkSubscriptionTierFeature } = useGlobalState()
  const {
    formData,
    handleFormDataChange,
    roles,
    isLoadingRoles,
    profile,
    user,
    token,
    isLoadingProfile,
    rootOrganizationId,
    refetchUserProfile,
  } = useEditUserDialog()

  const {
    ledgerAccounts,
    isLoading: isLoadingLedgerAccounts,
    refetch: refetchLedgerAccounts,
  } = useFetchLedgerAccounts(rootOrganizationId || '', token!, !!rootOrganizationId)

  // Team change wizard state
  const [wizardOpen, setWizardOpen] = useState(false)
  const [wizardStep, setWizardStep] = useState(0)
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null)
  const [showLedgerWarning, setShowLedgerWarning] = useState(false)
  const [newTeamDefaultLedgerId, setNewTeamDefaultLedgerId] = useState<string | null>(null)
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null)
  const [isLoadingTeamChange, setIsLoadingTeamChange] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success')

  // Ledger account change wizard state
  const [ledgerWizardOpen, setLedgerWizardOpen] = useState(false)
  const [selectedLedgerId, setSelectedLedgerId] = useState<string | null>(null)
  const [currentLedgerId, setCurrentLedgerId] = useState<string | null>(null)
  const [isLoadingLedgerChange, setIsLoadingLedgerChange] = useState(false)

  // Plans to be moved state
  const [activePlansToMove, setActivePlansToMove] = useState<any[]>([])

  // Fetch core settings for selected team when it changes
  const { coreSettings: selectedTeamCoreSettings } = useFetchOrganizationCoreSettings(
    selectedTeamId ? [selectedTeamId] : [],
    authToken || ''
  )

  useEffect(() => {
    if (defaultOrganizationId) {
      handleSetMemberRole(defaultOrganizationId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOrganizationId])

  useEffect(() => {
    if (rootOrganizationId && token && !ledgerAccounts) {
      refetchLedgerAccounts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootOrganizationId, token, ledgerAccounts]) // Remove the useCallback entirely

  // Extract default ledger account ID from selected team core settings
  useEffect(() => {
    if (selectedTeamCoreSettings && selectedTeamCoreSettings.length > 0) {
      const teamSettings = selectedTeamCoreSettings[0]
      setNewTeamDefaultLedgerId(teamSettings.defaultLedgerAccount || null)
    }
  }, [selectedTeamCoreSettings])

  // Calculate which plans will be moved when team selection changes
  useEffect(() => {
    if (wizardStep === 1 && selectedTeamId) {
      const now = new Date()
      const activePlans =
        user?.plans?.filter((plan) => {
          // Include plans that haven't ended yet
          return !plan.endDate || new Date(plan.endDate) > now
        }) || []
      setActivePlansToMove(activePlans)
    }
  }, [wizardStep, selectedTeamId, user?.plans])

  // Set default ledger account in create mode
  useEffect(() => {
    if (createMode && ledgerAccounts && ledgerAccounts.length > 0 && !formData.ledgerAccountId) {
      // Try to find the account with label "DEFAULT LEDGER"
      const defaultLedger = ledgerAccounts.find((account) => account.label === 'Default Account')

      // Use the default ledger if found, otherwise use the first account
      const accountToSet = defaultLedger || ledgerAccounts[0]

      handleFormDataChange({ ...formData, ledgerAccountId: accountToSet.id })
    }
  }, [createMode, ledgerAccounts, formData, handleFormDataChange])

  const handleMemberRoleSelectionChange = (orgId: string) => {
    // Guard against triggering when dialog is closing/resetting
    if (!profile || !token || !rootOrganizationId) {
      return
    }

    // Additional safeguard: check if form data is in valid state
    if (!formData.firstName && !formData.lastName && !formData.email && formData.assignedRoles.length === 0) {
      return
    }

    if (!createMode) {
      // Allow role changes without confirmation
    }

    handleSetMemberRole(orgId)
  }

  const handleSetMemberRole = (orgId: string) => {
    const memberRoleId = lookupMemberRoleId()
    if (!memberRoleId) {
      console.error('Member role ID not found')
      return
    }

    const currentRoles = formData.assignedRoles
    let adjustedRoles = handleRemoveMemberRole(handleRemoveGuestRole(currentRoles))
    const updatedRoles = [...adjustedRoles, { organizationId: orgId, roleId: memberRoleId }]

    handleFormDataChange({ ...formData, assignedRoles: updatedRoles })
    return
  }

  // const handleSetGuestRole = (orgId: string) => {
  //   const guestRoleId = lookupGuestRoleId()
  //   if (!guestRoleId) {
  //     console.error('Guest role ID not found')
  //     return
  //   }
  //   const currentRoles = formData.assignedRoles
  //   let adjustedRoles = handleRemoveMemberRole(handleRemoveGuestRole(currentRoles))
  //   const updatedRoles = [...adjustedRoles, { organizationId: orgId, roleId: guestRoleId }]

  //   handleFormDataChange({ ...formData, assignedRoles: updatedRoles })
  //   return
  // }

  const handleRemoveMemberRole = (assignedRoles: AssignedRole[]): AssignedRole[] => {
    const memberRoleId = lookupMemberRoleId()
    if (assignedRoles.length === 0) return assignedRoles

    const filteredRoles = assignedRoles.filter((role) => {
      if (role.roleId === memberRoleId && role.delete == null) {
        return false
      }
      return true
    })
    assignedRoles = filteredRoles

    return assignedRoles.map((role) => {
      if (role.roleId === memberRoleId) {
        return { ...role, delete: true }
      }
      return role
    })
  }

  const handleRemoveGuestRole = (assignedRoles: AssignedRole[]): AssignedRole[] => {
    const guestRoleId = lookupGuestRoleId()
    if (!guestRoleId) {
      console.error('Guest role not found')
      return assignedRoles
    }
    if (assignedRoles.length === 0) return assignedRoles

    const filteredRoles = assignedRoles.filter((role) => {
      if (role.roleId === guestRoleId && role.delete == null) {
        return false
      }
      return true
    })
    assignedRoles = filteredRoles

    return assignedRoles.map((role) => {
      if (role.roleId === guestRoleId) {
        return { ...role, delete: true }
      }
      return role
    })
  }

  const lookupMemberRoleId = (): string | null => {
    if (!isLoadingRoles && roles) {
      const role = lookupMemberRole(roles)
      if (role) {
        return role.id
      }
    }
    return null
  }

  const lookupGuestRoleId = (): string | null => {
    if (!isLoadingRoles && roles) {
      const role = lookupGuestRole(roles)
      if (role) {
        return role.id
      }
    }
    return null
  }

  const lookupMemberOrgId = (): string | null => {
    const memberRoleId = lookupMemberRoleId()
    const r = formData.assignedRoles.find((role) => role.roleId === memberRoleId && role.delete !== true)
    if (r) {
      return r.organizationId
    }
    return null
  }

  const lookupGuestOrgId = (): string | null => {
    const guestRoleId = lookupGuestRoleId()
    const r = formData.assignedRoles.find((role) => role.roleId === guestRoleId && role.delete !== true)
    if (r) {
      return r.organizationId
    }
    return null
  }

  const handleGuestRoleToggle = (checked: boolean) => {
    const guestRoleId = lookupGuestRoleId()
    const memberRoleId = lookupMemberRoleId()
    const orgId = rootOrganizationId
    if (orgId == null) {
      console.error('Root organization ID is null')
      return
    }
    if (checked) {
      const currentRoles = formData.assignedRoles
      let adjustedRoles = handleRemoveMemberRole(handleRemoveGuestRole(currentRoles))
      // We add both the Member default to RootOrg and Guest default to RootOrg
      const updatedRoles = [
        ...adjustedRoles,
        { organizationId: orgId, roleId: memberRoleId! },
        { organizationId: orgId, roleId: guestRoleId! },
      ]
      handleFormDataChange({ ...formData, assignedRoles: updatedRoles })
    } else {
      const currentRoles = formData.assignedRoles
      let adjustedRoles = handleRemoveGuestRole(currentRoles)
      const updatedRoles = [...adjustedRoles]
      handleFormDataChange({ ...formData, assignedRoles: updatedRoles })
    }

    return
  }

  const handleOpenWizard = () => {
    // Capture the current team ID before opening the wizard
    const currentTeam = lookupMemberOrgId()
    setCurrentTeamId(currentTeam)
    setSelectedTeamId(null)
    setWizardStep(0)
    setShowLedgerWarning(false)
    setNewTeamDefaultLedgerId(null)
    setWizardOpen(true)
  }

  const handleCloseWizard = () => {
    setWizardOpen(false)
    setSelectedTeamId(null)
    setWizardStep(0)
    setShowLedgerWarning(false)
    setCurrentTeamId(null)
    setNewTeamDefaultLedgerId(null)
  }

  const handleOpenLedgerWizard = () => {
    setCurrentLedgerId(formData.ledgerAccountId)
    setSelectedLedgerId(null)
    setLedgerWizardOpen(true)
  }

  const handleCloseLedgerWizard = () => {
    setLedgerWizardOpen(false)
    setSelectedLedgerId(null)
    setCurrentLedgerId(null)
  }

  const handleLedgerWizardConfirm = async () => {
    if (!selectedLedgerId) return

    setIsLoadingLedgerChange(true)
    try {
      // Make API request to change the user's ledger account
      const changeLedgerPayload = {
        newLedgerAccountId: selectedLedgerId,
      }

      await postWithToken(`users/${formData.id}/change-ledger-account`, token!, changeLedgerPayload)

      // Update the form data with the new ledger account
      handleFormDataChange({ ...formData, ledgerAccountId: selectedLedgerId })

      setSnackbarMessage('Ledger account changed successfully')
      setSnackbarSeverity('success')
      setSnackbarOpen(true)

      handleCloseLedgerWizard()
    } catch (error) {
      console.error('Error changing ledger account:', error)
      setSnackbarMessage(error instanceof Error ? error.message : 'Failed to change ledger account')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      setIsLoadingLedgerChange(false)
    }
  }

  const handleWizardNextStep = async () => {
    if (wizardStep === 0 && selectedTeamId) {
      // Moving from team selection to review page
      setWizardStep(1)
    } else if (wizardStep === 1) {
      // Apply the team change via API endpoint
      setIsLoadingTeamChange(true)
      try {
        const changeTeamPayload = {
          newTeamId: selectedTeamId,
          changeLedgerAccount:
            showLedgerWarning && newTeamDefaultLedgerId && newTeamDefaultLedgerId !== formData.ledgerAccountId,
          newLedgerAccountId: showLedgerWarning && newTeamDefaultLedgerId ? newTeamDefaultLedgerId : null,
        }

        await postWithToken(`users/${formData.id}/change-team`, token!, changeTeamPayload)

        // Update local form state to reflect the change
        handleSetMemberRole(selectedTeamId!)
        if (showLedgerWarning && newTeamDefaultLedgerId && newTeamDefaultLedgerId !== formData.ledgerAccountId) {
          handleFormDataChange({ ...formData, ledgerAccountId: newTeamDefaultLedgerId })
        }

        // Refetch the user profile to update plans with new organization assignments
        await refetchUserProfile()

        // Show success message
        setSnackbarMessage('Team changed successfully')
        setSnackbarSeverity('success')
        setSnackbarOpen(true)

        handleCloseWizard()
      } catch (error) {
        console.error('Error changing team:', error)
        setSnackbarMessage(error instanceof Error ? error.message : 'Failed to change team')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      } finally {
        setIsLoadingTeamChange(false)
      }
    }
  }

  // Helper function to get ledger account label by ID
  const getLedgerAccountLabel = (accountId: string | null): string => {
    if (!accountId || !ledgerAccounts) return 'Unknown'
    const account = ledgerAccounts.find((acc) => acc.id === accountId)
    return account?.label || accountId
  }

  // Make sure all the require data has loaded prior to showing the form
  if (isLoadingProfile || isLoadingLedgerAccounts || !ledgerAccounts) return null

  if (isLoadingRoles) return null

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="First Name *"
            variant="outlined"
            pill
            shrink
            value={formData.firstName}
            onChange={(e) => handleFormDataChange({ ...formData, firstName: e.target.value })}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Last Name *"
            variant="outlined"
            pill
            shrink
            value={formData.lastName}
            onChange={(e) => handleFormDataChange({ ...formData, lastName: e.target.value })}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Email *"
            variant="outlined"
            pill
            shrink
            value={formData.email}
            onChange={(e) => handleFormDataChange({ ...formData, email: e.target.value })}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Position (Optional)"
            variant="outlined"
            pill
            shrink
            value={formData.position}
            onChange={(e) => handleFormDataChange({ ...formData, position: e.target.value })}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <Box sx={{ flex: '0 0 75%' }}>
              <SingleOrganizationSelector
                userProfile={profile!}
                selectedOrganizationId={lookupMemberOrgId() || ''}
                handleChange={(value) => handleMemberRoleSelectionChange(value)}
                label={lookupGuestOrgId() != null ? 'Team Membership Unavailable' : 'Team Membership *'}
                labelBackgroundColor={mode === 'dark' ? '#747474' : '#ffffff'}
                disabled={lookupGuestOrgId() != null || !createMode}
                disableAutoSelection={
                  !formData.firstName && !formData.lastName && !formData.email && formData.assignedRoles.length === 0
                }
              />
            </Box>
            <Box sx={{ flex: '0 0 25%', display: 'flex', alignItems: 'flex-end' }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleOpenWizard}
                disabled={lookupGuestOrgId() != null || createMode}
                fullWidth
              >
                Change Team
              </Button>
            </Box>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={lookupGuestOrgId() != null}
              onChange={(event) => handleGuestRoleToggle(event.target.checked as boolean)}
              inputProps={{ 'aria-label': 'toggle switch' }}
            />
            <Typography variant="body1">Guest Member</Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DesktopDatePicker
            label="Start Date *"
            value={formData.startDate ? dayjs.utc(formData.startDate) : null}
            onChange={(newValue: Dayjs | null) => {
              if (newValue) {
                const dateString = newValue.format('YYYY-MM-DD')
                const utcDate = dayjs.utc(dateString)
                handleFormDataChange({ ...formData, startDate: utcDate })
              } else {
                handleFormDataChange({ ...formData, startDate: null })
              }
            }}
            format={DATE_FORMAT}
            slotProps={{
              textField: {
                variant: 'outlined',
                fullWidth: true,
                InputProps: {
                  readOnly: true,
                },
                InputLabelProps: { shrink: true },
                sx: {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '21px',
                    '& fieldset': {
                      borderRadius: '21px',
                    },
                  },
                },
                helperText: 'Click calendar icon to select date',
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DesktopDatePicker
            label="End Date (Optional)"
            value={formData.endDate ? dayjs.utc(formData.endDate) : null}
            onChange={(newValue: Dayjs | null) => {
              if (newValue) {
                const dateString = newValue.format('YYYY-MM-DD')
                const utcDate = dayjs.utc(dateString)
                handleFormDataChange({ ...formData, endDate: utcDate })
              } else {
                handleFormDataChange({ ...formData, endDate: null })
              }
            }}
            format={DATE_FORMAT}
            slotProps={{
              textField: {
                variant: 'outlined',
                fullWidth: true,
                InputProps: {
                  readOnly: true,
                },
                InputLabelProps: { shrink: true },
                sx: {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '21px',
                    '& fieldset': {
                      borderRadius: '21px',
                    },
                  },
                },
                helperText: 'Click calendar icon to select date',
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {checkSubscriptionTierFeature('INCENTIVE.ALL') && (
            <div>
              {createMode ? (
                <FormControl fullWidth variant="outlined">
                  <InputLabel
                    shrink
                    variant="outlined"
                    sx={{
                      backgroundColor: mode === 'dark' ? '#747474' : '#ffffff',
                      padding: '0 8px',
                      marginLeft: -6,
                    }}
                  >
                    Ledger Account *
                  </InputLabel>
                  <Select
                    id="ledger-account-select"
                    variant="outlined"
                    value={formData.ledgerAccountId}
                    onChange={(e) => handleFormDataChange({ ...formData, ledgerAccountId: e.target.value })}
                    fullWidth
                    style={{ borderRadius: '21px' }}
                  >
                    {Array.isArray(ledgerAccounts) &&
                      ledgerAccounts.map((account) => {
                        return (
                          <MenuItem key={account.id} value={account.id}>
                            {account.label}
                          </MenuItem>
                        )
                      })}
                  </Select>
                </FormControl>
              ) : (
                <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                  <Box sx={{ flex: '0 0 75%' }}>
                    <FormControl fullWidth variant="outlined" disabled>
                      <InputLabel
                        shrink
                        variant="outlined"
                        sx={{
                          backgroundColor: mode === 'dark' ? '#747474' : '#ffffff',
                          padding: '0 8px',
                          marginLeft: -6,
                        }}
                      >
                        Ledger Account *
                      </InputLabel>
                      <Select
                        variant="outlined"
                        value={formData.ledgerAccountId || ''}
                        fullWidth
                        style={{ borderRadius: '21px' }}
                      >
                        {formData.ledgerAccountId && (
                          <MenuItem value={formData.ledgerAccountId}>
                            {getLedgerAccountLabel(formData.ledgerAccountId)}
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: '0 0 25%', display: 'flex', alignItems: 'flex-end' }}>
                    <Button variant="outlined" color="primary" onClick={handleOpenLedgerWizard} fullWidth>
                      Change
                    </Button>
                  </Box>
                </Stack>
              )}
            </div>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="External ID (Optional)"
            variant="outlined"
            pill
            shrink
            value={formData.externalId}
            onChange={(e) => handleFormDataChange({ ...formData, externalId: e.target.value })}
            fullWidth
            placeholder="External Identifier"
          />
        </Grid>
      </Grid>

      {/* Ledger Account Change Dialog */}
      <Dialog open={ledgerWizardOpen} onClose={handleCloseLedgerWizard} maxWidth="sm" fullWidth>
        <DialogTitle>Change Ledger Account</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 2 }}>
            <Alert severity="warning">
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Ledger Account Change Implications
              </Typography>
              <Typography variant="body2">
                Changing a user's ledger account is a significant operation that affects financial records and
                compensation tracking. You will need to:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                <li>
                  <Typography variant="body2">
                    Reconcile compensation history between the old and new ledger accounts
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">Verify all accrual balances are correctly transferred</Typography>
                </li>
                <li>
                  <Typography variant="body2">Update related reports and financial records</Typography>
                </li>
              </Box>
            </Alert>

            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Ledger Account Details
              </Typography>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Current Ledger Account:
                  </Typography>
                  <Typography variant="body2">{getLedgerAccountLabel(currentLedgerId)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                    New Ledger Account:
                  </Typography>
                  <FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
                    <InputLabel
                      shrink
                      variant="outlined"
                      sx={{
                        backgroundColor: 'action.hover',
                        padding: '0 8px',
                        marginLeft: -6,
                      }}
                    >
                      Select Account
                    </InputLabel>
                    <Select
                      variant="outlined"
                      value={selectedLedgerId || ''}
                      onChange={(e) => setSelectedLedgerId(e.target.value)}
                      fullWidth
                      style={{ borderRadius: '21px' }}
                    >
                      {Array.isArray(ledgerAccounts) &&
                        ledgerAccounts
                          .filter((account) => account.id !== currentLedgerId)
                          .map((account) => {
                            return (
                              <MenuItem key={account.id} value={account.id}>
                                {account.label}
                              </MenuItem>
                            )
                          })}
                    </Select>
                  </FormControl>
                </Box>
              </Stack>
            </Box>

            <Alert severity="error">
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Manual Reconciliation Required
              </Typography>
              <Typography variant="body2">
                You are responsible for reconciling ledger entries and ensuring all compensation records are accurate
                after this change. The system will update the user's account assignment, but you must manually verify
                ledger balances and historical records.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLedgerWizard} variant="outlined" disabled={isLoadingLedgerChange}>
            Cancel
          </Button>
          <Button
            onClick={handleLedgerWizardConfirm}
            variant="contained"
            color="warning"
            disabled={!selectedLedgerId || isLoadingLedgerChange}
          >
            {isLoadingLedgerChange ? 'Changing...' : 'Confirm Change'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Change Wizard Dialog */}
      <Dialog open={wizardOpen} onClose={handleCloseWizard} maxWidth="sm" fullWidth>
        {wizardStep === 0 ? (
          <>
            <DialogTitle>Select New Team</DialogTitle>
            <DialogContent dividers>
              <Box sx={{ pt: 2 }}>
                <SingleOrganizationSelector
                  userProfile={profile!}
                  selectedOrganizationId={selectedTeamId || ''}
                  handleChange={(value) => {
                    setSelectedTeamId(value)
                  }}
                  label="Team *"
                  labelBackgroundColor={mode === 'dark' ? '#747474' : '#ffffff'}
                  disableAutoSelection={true}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseWizard} variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleWizardNextStep} variant="contained" disabled={!selectedTeamId}>
                Next
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Review Team Change</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 2 }}>
                {/* Current Team on Left, New Team on Right */}
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  {/* Current Team - Left */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Current Team:
                    </Typography>
                    <Typography variant="body1">
                      {profile?.roles?.find((role) => role.organization.id === currentTeamId)?.organization.name ||
                        'Not assigned'}
                    </Typography>
                  </Box>

                  {/* Arrow in center */}
                  <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.5 }}>
                    <Typography variant="body2" color="textSecondary">
                      →
                    </Typography>
                  </Box>

                  {/* New Team - Right */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      New Team:
                    </Typography>
                    <Typography variant="body1">
                      {profile?.roles?.find((role) => role.organization.id === selectedTeamId)?.organization.name || ''}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                {/* Active Plans that will be moved */}
                {activePlansToMove && activePlansToMove.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Plans to be Moved ({activePlansToMove.length})
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                      The following active compensation plans will be moved to the new team:
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '0.875rem',
                        }}
                      >
                        <thead>
                          <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600 }}>Current Team</th>
                            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600 }}>Effective Date</th>
                            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600 }}>End Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activePlansToMove.map((plan) => (
                            <tr key={plan.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                              <td style={{ padding: '8px' }}>{plan.organizationName || 'Unknown'}</td>
                              <td style={{ padding: '8px' }}>
                                {plan.effectiveDate ? dayjs.utc(plan.effectiveDate).format(DATE_FORMAT) : '—'}
                              </td>
                              <td style={{ padding: '8px' }}>
                                {plan.endDate ? dayjs.utc(plan.endDate).format(DATE_FORMAT) : 'No end date'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      <Typography variant="body2">
                        All active plans will be transferred to the new team. Plan configurations and accrual details
                        will remain intact.
                      </Typography>
                    </Alert>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Only show ledger section if they differ */}
                {newTeamDefaultLedgerId && newTeamDefaultLedgerId !== formData.ledgerAccountId ? (
                  <>
                    <Alert severity="warning">
                      Changing the team membership will move the user to a different organization. All active
                      compensation plans will be transferred to the new team.
                    </Alert>

                    {/* Ledger Account Information - only when different */}
                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Ledger Account Details
                      </Typography>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Current Ledger Account:
                          </Typography>
                          <Typography variant="body2">{getLedgerAccountLabel(formData.ledgerAccountId)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            {(selectedTeamId &&
                              profile?.roles?.find((role) => role.organization.id === selectedTeamId)?.organization
                                .name) ||
                              'New Team'}
                            's Default Ledger Account:
                          </Typography>
                          <Typography variant="body2">{getLedgerAccountLabel(newTeamDefaultLedgerId)}</Typography>
                        </Box>
                      </Stack>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      This team has a different default ledger account. Would you like to update the user's ledger
                      account to match?
                    </Alert>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        checked={showLedgerWarning}
                        onChange={(e) => {
                          setShowLedgerWarning(e.target.checked)
                        }}
                      />
                      <Typography variant="body2">Change ledger account</Typography>
                    </Box>
                    {showLedgerWarning && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Ledger Reconciliation Required:</strong> Moving a user to a different ledger account
                          may require adjustments to reconcile their compensation history. Please review and adjust
                          ledger entries as needed to maintain accurate records.
                        </Typography>
                      </Alert>
                    )}
                  </>
                ) : null}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setWizardStep(0)} variant="outlined" disabled={isLoadingTeamChange}>
                Back
              </Button>
              <Button onClick={handleCloseWizard} variant="outlined" color="warning" disabled={isLoadingTeamChange}>
                Cancel
              </Button>
              <Button onClick={handleWizardNextStep} variant="contained" color="primary" disabled={isLoadingTeamChange}>
                {isLoadingTeamChange ? 'Changing...' : 'Confirm Change'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default UserInformationForm
